import React, { useState, useEffect, useRef } from 'react';
import { Plus, ArrowLeft, Paperclip, Send, X, Download, AlertCircle } from 'lucide-react';
import { useTheme } from './shared';
import * as api from './api';

const SupportTab = () => {
  const t = useTheme();

  // Determine API base URL based on environment
  const isProduction = window.location.protocol === 'https:' || window.location.port === '' || window.location.port === '80' || window.location.port === '443';
  const BASE_URL = isProduction ? '/api' : `http://${window.location.hostname}:5000/api`;

  // State management
  const [view, setView] = useState('list'); // 'list' or 'detail'
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Create ticket form state
  const [createForm, setCreateForm] = useState({
    subject: '',
    text: '',
    files: []
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Reply form state
  const [replyForm, setReplyForm] = useState({
    text: '',
    files: []
  });
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyError, setReplyError] = useState(null);

  // Refs
  const fileInputCreateRef = useRef(null);
  const fileInputReplyRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Get JWT token
  const getToken = () => localStorage.getItem('inmonex_token');

  // API request helper
  const apiRequest = async (endpoint, method = 'GET', body = null) => {
    const token = getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const options = {
      method,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };

    if (body) {
      options.body = body;
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, options);
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    return response.json();
  };

  // Fetch tickets list
  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiRequest('/tickets');
      setTickets(data);
      await fetchUnreadCount();
    } catch (err) {
      setError('Не удалось загрузить тикеты');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const data = await apiRequest('/tickets/unread-count');
      setUnreadCount(data.count || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  // Fetch ticket detail
  const fetchTicketDetail = async (ticketId) => {
    try {
      const data = await apiRequest(`/tickets/${ticketId}`);
      setSelectedTicket(data);
      await fetchUnreadCount();
      setView('detail');
    } catch (err) {
      setError('Не удалось загрузить детали тикета');
      console.error(err);
    }
  };

  // Create new ticket
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!createForm.subject.trim() || !createForm.text.trim()) {
      setCreateError('Тема и сообщение обязательны');
      return;
    }

    try {
      setCreateLoading(true);
      setCreateError(null);

      const formData = new FormData();
      formData.append('subject', createForm.subject);
      formData.append('text', createForm.text);
      createForm.files.forEach(file => {
        formData.append('files', file);
      });

      const token = getToken();
      const response = await fetch(`${BASE_URL}/tickets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Не удалось создать тикет');
      }

      const newTicket = await response.json();
      setCreateForm({ subject: '', text: '', files: [] });
      setShowCreateModal(false);
      await fetchTickets();
      await fetchTicketDetail(newTicket.id);
    } catch (err) {
      setCreateError(err.message || 'Не удалось создать тикет');
      console.error(err);
    } finally {
      setCreateLoading(false);
    }
  };

  // Reply to ticket
  const handleReplyToTicket = async (e) => {
    e.preventDefault();
    if (!replyForm.text.trim()) {
      setReplyError('Сообщение обязательно');
      return;
    }

    try {
      setReplyLoading(true);
      setReplyError(null);

      const formData = new FormData();
      formData.append('text', replyForm.text);
      replyForm.files.forEach(file => {
        formData.append('files', file);
      });

      const token = getToken();
      const response = await fetch(`${BASE_URL}/tickets/${selectedTicket.id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Не удалось отправить ответ');
      }

      setReplyForm({ text: '', files: [] });
      await fetchTicketDetail(selectedTicket.id);
    } catch (err) {
      setReplyError(err.message || 'Не удалось отправить ответ');
      console.error(err);
    } finally {
      setReplyLoading(false);
    }
  };

  // Handle file additions
  const handleAddFiles = (files, isReply = false) => {
    const newFiles = Array.from(files);
    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024; // 10MB

    const validFiles = newFiles.filter(file => {
      if (file.size > maxSize) {
        setCreateError(`Файл ${file.name} превышает лимит 10МБ`);
        return false;
      }
      return true;
    });

    if (isReply) {
      const totalFiles = replyForm.files.length + validFiles.length;
      if (totalFiles > maxFiles) {
        setReplyError(`Максимум ${maxFiles} файлов`);
        return;
      }
      setReplyForm(prev => ({
        ...prev,
        files: [...prev.files, ...validFiles]
      }));
    } else {
      const totalFiles = createForm.files.length + validFiles.length;
      if (totalFiles > maxFiles) {
        setCreateError(`Максимум ${maxFiles} файлов`);
        return;
      }
      setCreateForm(prev => ({
        ...prev,
        files: [...prev.files, ...validFiles]
      }));
    }
  };

  // Remove file from form
  const removeFile = (index, isReply = false) => {
    if (isReply) {
      setReplyForm(prev => ({
        ...prev,
        files: prev.files.filter((_, i) => i !== index)
      }));
    } else {
      setCreateForm(prev => ({
        ...prev,
        files: prev.files.filter((_, i) => i !== index)
      }));
    }
  };

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedTicket?.messages]);

  // Auto-refresh ticket detail every 15 seconds
  useEffect(() => {
    if (view !== 'detail' || !selectedTicket) return;

    const interval = setInterval(() => {
      fetchTicketDetail(selectedTicket.id);
    }, 15000);

    return () => clearInterval(interval);
  }, [view, selectedTicket?.id]);

  // Initial load
  useEffect(() => {
    fetchTickets();
  }, []);

  // Status badge styles
  const getStatusColor = (status) => {
    const colors = {
      new: '#6366f1',
      in_progress: '#f59e0b',
      resolved: '#22c55e',
      closed: '#64748b'
    };
    return colors[status] || '#6366f1';
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: 'Новый',
      in_progress: 'В работе',
      resolved: 'Решён',
      closed: 'Закрыт'
    };
    return labels[status] || status;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Только что';
    if (diffMins < 60) return `${diffMins}м назад`;
    if (diffHours < 24) return `${diffHours}ч назад`;
    if (diffDays < 7) return `${diffDays}д назад`;

    return date.toLocaleDateString('ru-RU', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // RENDER: Ticket List View
  if (view === 'list') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: t.bgCard,
        color: t.text
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${t.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600' }}>
              Поддержка
            </h2>
            <p style={{ margin: 0, fontSize: '13px', color: t.textSec }}>
              {tickets.length} {tickets.length === 1 ? 'тикет' : 'тикетов'}
              {unreadCount > 0 && ` • ${unreadCount} непрочитанных`}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#4f46e5'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#6366f1'}
          >
            <Plus size={18} />
            Новый вопрос
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: t.accentRed + '15',
              borderRadius: '6px',
              marginBottom: '16px',
              color: t.accentRed
            }}>
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              fontSize: '14px',
              color: t.textSec
            }}>
              Загрузка тикетов...
            </div>
          ) : tickets.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              textAlign: 'center',
              color: t.textMuted
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>📋</div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                У вас пока нет обращений
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  marginTop: '16px',
                  padding: '8px 16px',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#4f46e5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6366f1'}
              >
                Создать первое обращение
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {tickets.map(ticket => (
                <button
                  key={ticket.id}
                  onClick={() => fetchTicketDetail(ticket.id)}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: t.bgInput,
                    border: `1px solid ${t.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                    color: t.text
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = t.accent;
                    e.currentTarget.style.backgroundColor = t.bgCard;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = t.border;
                    e.currentTarget.style.backgroundColor = t.bgInput;
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '14px',
                          fontWeight: '500',
                          color: t.text
                        }}>
                          {ticket.subject}
                        </h3>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          backgroundColor: getStatusColor(ticket.status),
                          color: 'white',
                          borderRadius: '3px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {getStatusLabel(ticket.status)}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        fontSize: '12px',
                        color: t.textSec
                      }}>
                        <span>{formatDate(ticket.updatedAt)}</span>
                        <span>{ticket._count?.messages || 0} сообщений</span>
                      </div>
                    </div>
                    {ticket.unreadCount > 0 && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#6366f1',
                        borderRadius: '50%',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        flexShrink: 0
                      }}>
                        {ticket.unreadCount}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Create Ticket Modal */}
        {showCreateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: t.bgCard,
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: `1px solid ${t.border}`
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: t.text }}>
                  Новое обращение
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateError(null);
                  }}
                  style={{
                    background: '#fee2e2',
                    border: '2px solid #b91c1c',
                    borderRadius: 10,
                    padding: 5,
                    color: '#b91c1c',
                    cursor: 'pointer'
                  }}
                >
                  <X size={18} />
                </button>
              </div>

              {createError && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: t.accentRed + '15',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  color: t.accentRed,
                  fontSize: '13px'
                }}>
                  {createError}
                </div>
              )}

              <form onSubmit={handleCreateTicket}>
                {/* Subject Input */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '500',
                    marginBottom: '6px',
                    color: t.textSec
                  }}>
                    Тема
                  </label>
                  <input
                    type="text"
                    value={createForm.subject}
                    onChange={(e) => setCreateForm(prev => ({
                      ...prev,
                      subject: e.target.value
                    }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: t.bgInput,
                      border: `1px solid ${t.border}`,
                      borderRadius: '6px',
                      color: t.text,
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = t.accent}
                    onBlur={(e) => e.target.style.borderColor = t.border}
                    placeholder="напр., Вопрос по оплате"
                  />
                </div>

                {/* Message Textarea */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '500',
                    marginBottom: '6px',
                    color: t.textSec
                  }}>
                    Сообщение
                  </label>
                  <textarea
                    value={createForm.text}
                    onChange={(e) => setCreateForm(prev => ({
                      ...prev,
                      text: e.target.value
                    }))}
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '10px 12px',
                      backgroundColor: t.bgInput,
                      border: `1px solid ${t.border}`,
                      borderRadius: '6px',
                      color: t.text,
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = t.accent}
                    onBlur={(e) => e.target.style.borderColor = t.border}
                    placeholder="Опишите вашу проблему подробно..."
                  />
                </div>

                {/* Files */}
                {createForm.files.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: '500',
                      marginBottom: '6px',
                      color: t.textSec
                    }}>
                      Прикреплённые файлы ({createForm.files.length}/5)
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {createForm.files.map((file, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 10px',
                            backgroundColor: t.bgInput,
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: t.textSec
                          }}
                        >
                          <span>{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: t.accentRed,
                              padding: '4px'
                            }}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* File Input */}
                <div style={{ marginBottom: '20px' }}>
                  <button
                    type="button"
                    onClick={() => fileInputCreateRef.current?.click()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      backgroundColor: t.bgInput,
                      border: `1px solid ${t.border}`,
                      borderRadius: '6px',
                      color: t.textSec,
                      cursor: 'pointer',
                      fontSize: '13px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = t.accent;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = t.border;
                    }}
                  >
                    <Paperclip size={16} />
                    Прикрепить файлы
                  </button>
                  <input
                    ref={fileInputCreateRef}
                    type="file"
                    multiple
                    onChange={(e) => handleAddFiles(e.target.files)}
                    style={{ display: 'none' }}
                  />
                </div>

                {/* Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreateError(null);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: t.bgInput,
                      border: `1px solid ${t.border}`,
                      borderRadius: '6px',
                      color: t.textSec,
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = t.accent;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = t.border;
                    }}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      backgroundColor: createLoading ? '#475569' : '#6366f1',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      cursor: createLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!createLoading) e.currentTarget.style.backgroundColor = '#4f46e5';
                    }}
                    onMouseLeave={(e) => {
                      if (!createLoading) e.currentTarget.style.backgroundColor = '#6366f1';
                    }}
                  >
                    {createLoading ? 'Отправка...' : 'Отправить'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // RENDER: Ticket Detail View
  if (view === 'detail' && selectedTicket) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: t.bgCard,
        color: t.text
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${t.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={() => {
              setView('list');
              setSelectedTicket(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: t.textSec,
              padding: '4px',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = t.accent}
            onMouseLeave={(e) => e.currentTarget.style.color = t.textSec}
          >
            <ArrowLeft size={20} />
          </button>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600' }}>
              {selectedTicket.subject}
            </h2>
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              backgroundColor: getStatusColor(selectedTicket.status),
              color: 'white',
              borderRadius: '3px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {getStatusLabel(selectedTicket.status)}
            </span>
          </div>
        </div>

        {/* Messages Area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
            selectedTicket.messages.map((message, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: message.isAdmin ? 'flex-start' : 'flex-end',
                  gap: '8px'
                }}
              >
                <div style={{
                  maxWidth: '70%',
                  padding: '12px 14px',
                  backgroundColor: message.isAdmin ? t.bgInput : '#6366f1',
                  borderRadius: '6px',
                  wordWrap: 'break-word'
                }}>
                  <p style={{
                    margin: '0 0 6px 0',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    color: message.isAdmin ? t.text : 'white'
                  }}>
                    {message.text}
                  </p>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: '6px'
                  }}>
                    <span style={{
                      fontSize: '11px',
                      color: message.isAdmin ? t.textMuted : '#e0e7ff',
                      opacity: 0.8
                    }}>
                      {formatTime(message.createdAt)}
                    </span>
                    {message.isAdmin && (
                      <span style={{
                        fontSize: '11px',
                        color: t.textMuted,
                        fontWeight: '500'
                      }}>
                        Поддержка
                      </span>
                    )}
                  </div>
                  {message.attachments && message.attachments.length > 0 && (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: `1px solid ${message.isAdmin ? t.border : '#818cf8'}`
                    }}>
                      {message.attachments.map((attachment, attIndex) => (
                        <a
                          key={attIndex}
                          href="#"
                          onClick={(e) => { e.preventDefault(); api.downloadAuthedFile(attachment.url, attachment.name || attachment.filename).catch(err => alert(err.message || 'Не удалось скачать файл')); }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 0',
                            color: message.isAdmin ? t.textSec : '#e0e7ff',
                            fontSize: '12px',
                            textDecoration: 'none',
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                          <Download size={14} />
                          {attachment.filename}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: t.textMuted,
              fontSize: '14px'
            }}>
              Сообщений пока нет
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply Area */}
        <div style={{
          padding: '16px 20px',
          borderTop: `1px solid ${t.border}`,
          backgroundColor: t.bg
        }}>
          {replyError && (
            <div style={{
              padding: '10px 12px',
              backgroundColor: t.accentRed + '15',
              borderRadius: '4px',
              marginBottom: '12px',
              color: t.accentRed,
              fontSize: '13px'
            }}>
              {replyError}
            </div>
          )}

          <form onSubmit={handleReplyToTicket}>
            {/* Reply Textarea */}
            <div style={{ marginBottom: '12px' }}>
              <textarea
                value={replyForm.text}
                onChange={(e) => setReplyForm(prev => ({
                  ...prev,
                  text: e.target.value
                }))}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px 12px',
                  backgroundColor: t.bgInput,
                  border: `1px solid ${t.border}`,
                  borderRadius: '6px',
                  color: t.text,
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = t.accent}
                onBlur={(e) => e.target.style.borderColor = t.border}
                placeholder="Введите ваш ответ..."
              />
            </div>

            {/* Reply Files */}
            {replyForm.files.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  {replyForm.files.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        backgroundColor: t.bgInput,
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: t.textSec
                      }}
                    >
                      <span>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index, true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: t.accentRed,
                          padding: '0'
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center'
            }}>
              <button
                type="button"
                onClick={() => fileInputReplyRef.current?.click()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: t.bgInput,
                  border: `1px solid ${t.border}`,
                  borderRadius: '6px',
                  color: t.textSec,
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = t.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = t.border;
                }}
              >
                <Paperclip size={16} />
                Прикрепить
              </button>
              <input
                ref={fileInputReplyRef}
                type="file"
                multiple
                onChange={(e) => handleAddFiles(e.target.files, true)}
                style={{ display: 'none' }}
              />
              <button
                type="submit"
                disabled={replyLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginLeft: 'auto',
                  padding: '8px 14px',
                  backgroundColor: replyLoading ? '#475569' : '#6366f1',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: replyLoading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!replyLoading) e.currentTarget.style.backgroundColor = '#4f46e5';
                }}
                onMouseLeave={(e) => {
                  if (!replyLoading) e.currentTarget.style.backgroundColor = '#6366f1';
                }}
              >
                {replyLoading ? (
                  <>Отправка...</>
                ) : (
                  <>
                    <Send size={16} />
                    Отправить
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return null;
};

export default SupportTab;
